import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { isPieceUnit, normalizeUnit } from '../../utils/units';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSave, faArrowRight, faArrowLeft, faPlus, faTrash, faEye, faPaperclip } from '@fortawesome/free-solid-svg-icons';
import { UI_COLORS, colorHelpers } from '../../utils/colors';
import { validateNewCustomerData, validateOrderItems } from '../../utils/validate';
import customersService from '../../services/customersService';
import locationsService from '../../services/locationsService';
import productSizesService from '../../services/productSizesServices';
import warehouseService from '../../services/warehouseService';
import purchaseOrderService from '../../services/purchaseOrderService';
import SearchableSelect from '../common/SearchableSelect';
import AddLocationModal from '../locations/AddLocationModal';
import CustomerWarehouseForm from '../customers/CustomerWarehouseForm';
import AttachmentsPanel from '../common/AttachmentsPanel';
import { useTranslation } from 'react-i18next';
import {
    buildDeliveryLocationOptions,
    buildCustomerAddressDraft,
    isSyntheticLocationValue,
} from '../../utils/deliveryLocationOptions';
import useCustomerDeliveryLocation from '../../hooks/useCustomerDeliveryLocation';


const PAYMENT_TERMS_OPTIONS = [
    '100% Advance',
    'Against Invoice',
    'Against Delivery',
    '7 Days',
    '10 Days',
    '15 Days',
    '25 Days',
    '30 Days',
    '45 Days',
    '60 Days',
    'Against LC/Bill Discounting',
    'Others',
].map(v => ({ value: v, label: v }));

const sanitizeNumber = (v) => {
    if (v === null || v === undefined || v === '') return '';
    const s = String(v).replace(/[^0-9.]/g, '');
    return s;
};

const emptyItem = () => ({ product_size_id: '', unit: 'kg', length: '6', quantity: '', price: '', remark: '', pieces: '', load_by_pieces: false });

// Unit vocabulary: staff uses 'm', portal/BE uses 'meters'; treat both as
// length-based. 'kg' is weight-based; 'pieces'/'pcs' are already piece counts.
const isLengthUnit = (u) => u === 'm' || u === 'meters';
const isWeightUnit = (u) => u === 'kg';

const INDIAN_STATES = [
    'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
    'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
    'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
    'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
    'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
    'Andaman and Nicobar Islands', 'Chandigarh', 'Dadra and Nagar Haveli and Daman and Diu',
    'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Lakshadweep', 'Puducherry',
].map(s => ({ value: s, label: s }));

// Helper to get tomorrow's date in YYYY-MM-DD format for native date picker
const getTomorrowDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const y = tomorrow.getFullYear();
    const m = String(tomorrow.getMonth() + 1).padStart(2, '0');
    const d = String(tomorrow.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

// Helper to ensure YYYY-MM-DD for payload
const formatDateForPayload = (dateStr) => {
    if (!dateStr) return null;
    if (dateStr.includes('/')) {
        const parts = dateStr.split('/');
        if (parts.length === 3) {
            const [d, m, y] = parts;
            return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
        }
    }
    // If it already has hyphens or is another format, return as is (assuming YYYY-MM-DD)
    return dateStr;
};

const formatBytes = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const PurchaseOrdersForm = ({ isOpen, onClose, onSubmit, onSuccess, order = null, orderId = null, loading = false }) => {
    const { t } = useTranslation();
    const { resolving, resolveCustomerAddress } = useCustomerDeliveryLocation();

    const [currentOrder, setCurrentOrder] = useState(order);
    const lastFetchedIdRef = useRef(null);
    const [showPendingList, setShowPendingList] = useState(false);
    const pendingFileInputRef = useRef(null);

    if (!window.purchaseOrdersFormCache) window.purchaseOrdersFormCache = { suppliers: null, locations: null, productSizes: null, warehouses: null, ordersById: {}, loaded: false, loading: false };
    const cache = window.purchaseOrdersFormCache;

    const [suppliers, setSuppliers] = useState([]);
    // Full detail of the selected supplier; the list endpoint omits gst_number
    // and address lines, so we fetch the detail on selection.
    const [selectedSupplierDetail, setSelectedSupplierDetail] = useState(null);
    const [selectedCustomerDetail, setSelectedCustomerDetail] = useState(null);
    // Open when the customer's address is missing a part (usually city) that must be
    // collected before it can become a delivery location.
    const [showAddressPrompt, setShowAddressPrompt] = useState(false);
    const [customers, setCustomers] = useState([]);
    const [locations, setLocations] = useState([]);
    const [productSizes, setProductSizes] = useState([]);
    const [warehouses, setWarehouses] = useState([]);
    const [dropdownsLoading, setDropdownsLoading] = useState(false);
    const [productSearchLoading, setProductSearchLoading] = useState(false);
    const productSearchRef = useRef(null);
    const [supplierSearchLoading, setSupplierSearchLoading] = useState(false);
    const supplierSearchRef = useRef(null);
    const [customerSearchLoading, setCustomerSearchLoading] = useState(false);
    const customerSearchRef = useRef(null);
    const customerActiveTermRef = useRef('');
    const [locationSearchLoading, setLocationSearchLoading] = useState(false);
    const locationSearchRef = useRef(null);
    const locationActiveTermRef = useRef('');
    const productActiveTermRef = useRef('');
    const formItemsRef = useRef([]);

    const initialMode = order || orderId ? 'existing' : 'existing';
    const [mode, setMode] = useState(initialMode);
    const [step, setStep] = useState(0);

    const [formOrder, setFormOrder] = useState(() => ({
        supplier_id: order?.supplier_id ?? '',
        shipping_destination: order?.shipping_destination ?? 'warehouse',
        warehouse_id: order?.warehouse_id ?? '',
        customer_id: order?.customer_id ?? '',
        delivery_location_id: order?.delivery_location_id ?? '',
        contact_person: order?.contact_person ?? '',
        mobile_number: order?.mobile_number ?? '',
        expected_delivery_date: order?.expected_delivery_date ?? getTomorrowDate(),
        payment_terms: order?.payment_terms ?? '',
        discount_type: order?.discount_type ?? 'percentage',
        discount_value: order?.discount_value ?? '',
        gst_included: order?.gst_included ?? false,
        pricing_option: order?.pricing_option ?? 'all_inclusive',

        sms_notification: order?.sms_notification ?? false,
        remark: order?.remark ?? '',
        po_internal_remark: order?.po_internal_remark ?? '',
        items: Array.isArray(order?.items) && order.items.length > 0 ? order.items.map(i => ({
            product_size_id: i.product_size_id || '',
            unit: normalizeUnit(i.unit),
            length: i.length || '',
            quantity: i.quantity || '',
            price: i.price || '',
            remark: i.remark || '',
            pieces: i.quantity_per_pieces || i.quantity_in_pieces || '',
            load_by_pieces: !!(i.load_by_pieces)
        })) : [emptyItem()],
    }));

    const formOrderRef = useRef(formOrder);
    useEffect(() => {
        formOrderRef.current = formOrder;
        formItemsRef.current = formOrder.items;
    }, [formOrder]);

    // defaultNewSupplier created with useMemo inside component to capture localized placeholders
    const defaultNewSupplier = useMemo(() => ({
        owner_name: '',
        company_name: '',
        tally_name: '',
        gst_number: '',
        contact_person: '',
        phone_number_1: '',
        email: '',
        address_line1: '',
        city: '',
        state: '',
        country: t('purchaseOrdersModule.locations.defaults.country'),
        zip_code: '',
        credit_period: 0,
        reference_party_name: '',
        isAuthorized: true,
        sister_company: false,
    }), [t]);

    const [newSupplier, setNewSupplier] = useState(defaultNewSupplier);
    const [errors, setErrors] = useState({});
    const [loadingOrder, setLoadingOrder] = useState(false);
    // Set when an edit-mode detail fetch fails/404s, so we show a "couldn't load"
    // message instead of spinning forever (the PO may have been deleted).
    const [loadOrderError, setLoadOrderError] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [pendingFiles, setPendingFiles] = useState([]);
    const [showAddLocationModal, setShowAddLocationModal] = useState(false);
    const [locationAddInitialValue, setLocationAddInitialValue] = useState('');

    const fetchOrderById = useCallback(async (id) => {
        if (!id) return null;
        if (lastFetchedIdRef.current === id) return null;
        lastFetchedIdRef.current = id;
        try {
            const result = await purchaseOrderService.getPurchaseOrderById(id, { force: true });
            if (!result || !result.success || !result.data) {
                lastFetchedIdRef.current = null;  // clear so a reopen retries
                return null;
            }
            const o = result.data;

            // Updated: Removed caching to ensure fresh data on every edit
            // cache.ordersById = cache.ordersById || {};
            // cache.ordersById[id] = o;

            setCurrentOrder(o);
            mapOrderToForm(o);
            return o;
        } catch (err) {
            lastFetchedIdRef.current = null;  // clear so a reopen retries
            return null;
        }
    }, []);

    const mapOrderToForm = (o) => {
        setFormOrder({
            supplier_id: o.supplier_id ?? '',
            shipping_destination: o.shipping_destination ?? 'warehouse',
            warehouse_id: o.warehouse_id ?? '',
            customer_id: o.customer_id ?? '',
            delivery_location_id: o.delivery_location_id ?? '',
            contact_person: o.contact_person ?? '',
            mobile_number: o.mobile_number ?? '',
            expected_delivery_date: o.expected_delivery_date ?? getTomorrowDate(),
            payment_terms: o.payment_terms !== undefined && o.payment_terms !== null ? String(o.payment_terms) : '',
            discount_type: o.discount_type ?? 'percentage',
            discount_value: o.discount_value !== undefined && o.discount_value !== null ? String(o.discount_value) : '',
            gst_included: !!o.gst_included,
            pricing_option: o.pricing_option ?? 'all_inclusive',

            sms_notification: !!o.sms_notification,
            remark: o.remark ?? '',
            po_internal_remark: o.po_internal_remark ?? '',
            items: Array.isArray(o.items) && o.items.length > 0 ? o.items.map(it => ({
                product_size_id: it.product_size_id ?? '',
                unit: normalizeUnit(it.unit) || 'kg',
                length: it.length !== undefined && it.length !== null ? String(it.length) : '6',
                quantity: it.quantity !== undefined && it.quantity !== null ? String(it.quantity) : '',
                price: it.price !== undefined && it.price !== null ? String(it.price) : '',
                remark: it.remark ?? '',
                load_by_pieces: !!(it.load_by_pieces),
                pieces: (it.quantity_per_pieces ?? it.quantity_in_pieces) !== undefined && (it.quantity_per_pieces ?? it.quantity_in_pieces) !== null ? String(it.quantity_per_pieces ?? it.quantity_in_pieces) : '',
                product_weight: (it.standard_weight ?? it.product_weight) !== undefined && (it.standard_weight ?? it.product_weight) !== null ? String(it.standard_weight ?? it.product_weight) : '',
                standard_length: it.standard_length !== undefined && it.standard_length !== null ? String(it.standard_length) : '',
            })) : [emptyItem()],
        });
    };

    const activeOrderData = currentOrder || order;

    // Augment suppliers list with the order's supplier if not present (handles edit with paginated dropdowns)
    const augmentedSuppliers = useMemo(() => {
        if (!activeOrderData?.supplier_id) return suppliers;
        if (suppliers.some(s => String(s.id) === String(activeOrderData.supplier_id))) return suppliers;
        return [
            { id: activeOrderData.supplier_id, company_name: activeOrderData.supplier_name || activeOrderData.tally_name, owner_name: activeOrderData.supplier_name, tally_name: activeOrderData.tally_name },
            ...suppliers,
        ];
    }, [suppliers, activeOrderData]);

    // Augment customers list with the order's customer if not present (handles edit with paginated dropdowns)
    const augmentedCustomers = useMemo(() => {
        if (!activeOrderData?.customer_id) return customers;
        if (customers.some(c => String(c.id) === String(activeOrderData.customer_id))) return customers;
        return [
            { id: activeOrderData.customer_id, company_name: activeOrderData.customer_name, owner_name: activeOrderData.customer_name },
            ...customers,
        ];
    }, [customers, activeOrderData]);
    
    const getProductForCalc = useCallback((id) => {
        const pid = String(id);
        if (!pid) return null;
        return (productSizes || []).find(p => String(p.id) === pid) || (cache.productSizes || []).find(p => String(p.id) === pid) || (activeOrderData?.items || []).find(it => String(it.product_size_id) === pid);
    }, [productSizes, cache.productSizes, activeOrderData]);



    const loadDropdownsRef = useRef(null);
    const loadDropdowns = useCallback(async () => {
        if (cache.loaded) {
            if (suppliers.length === 0 && cache.suppliers) setSuppliers(cache.suppliers);
            if (locations.length === 0 && cache.locations) setLocations(cache.locations);
            if (warehouses.length === 0 && cache.warehouses) setWarehouses(cache.warehouses);
            // SKUs are never served from the window cache: a price edited in Product
            // Sizes must show on the very next form open (P1-4).
            productSizesService.getAllProductSizes({}, { force: true })
                .then((resp) => {
                    const raw = resp?.data;
                    const data = Array.isArray(raw) ? raw
                        : (Array.isArray(raw?.items) ? raw.items
                        : (Array.isArray(raw?.product_sizes) ? raw.product_sizes : []));
                    cache.productSizes = data;
                    // A search typed meanwhile owns the dropdown; the cache is enough for it.
                    if (!productActiveTermRef.current) setProductSizes(data);
                })
                .catch(() => { /* keep whatever is already shown */ });

            // Even if already loaded (cache hit), check if we need to set defaults for a new order
            if (!order && !orderId && cache.warehouses && cache.warehouses.length > 0) {
                const primaryWh = cache.warehouses.find(w => 
                    (w.name || '').toLowerCase().includes('primary') || 
                    (w.name || '').toLowerCase().includes('main') ||
                    (w.alias_name || '').toLowerCase().includes('primary')
                ) || cache.warehouses[0];

                if (primaryWh) {
                    setFormOrder(prev => {
                        // Only set if not already set (e.g., fresh open of "Add" modal)
                        if (prev.warehouse_id) return prev;
                        const updates = { warehouse_id: primaryWh.id };
                        
                        const whName = (primaryWh.name || '').toLowerCase();
                        const primaryLoc = (cache.locations || []).find(l => 
                            (l.delivery_location || '').toLowerCase().includes('primary') ||
                            (l.delivery_location || '').toLowerCase().includes(whName)
                        ) || (cache.locations || [])[0];

                        if (primaryLoc) {
                            updates.delivery_location_id = primaryLoc.id;
                        }
                        return { ...prev, ...updates };
                    });
                }
            }
            return;
        }
        if (cache.loading) return;
        cache.loading = true;
        setDropdownsLoading(true);
        try {
            const [custResp, locResp, psResp, whResp] = await Promise.all([
                customersService.getCustomers({ is_active: true }).catch(() => ({ data: [] })),
                locationsService.getLocations().catch(() => ({ data: [] })),
                // Fresh SKUs every time — a cached list hid price edits (P1-4).
                productSizesService.getAllProductSizes({}, { force: true }).catch(() => ({ data: [] })),
                warehouseService.getAllWarehouses().catch(() => ({ data: [] })),
            ]);

            const customersData = Array.isArray(custResp?.data) ? custResp.data : Array.isArray(custResp) ? custResp : [];
            // Filter for suppliers - Removed as per request to show all
            const suppliersData = customersData; 
            const allCustomers = customersData; // Keep all for customer selection

            const locationsData = Array.isArray(locResp?.data) ? locResp.data : Array.isArray(locResp) ? locResp : [];
            const psRaw = psResp?.data;
            const productSizesData = Array.isArray(psRaw) ? psRaw 
                : (Array.isArray(psRaw?.items) ? psRaw.items 
                : (Array.isArray(psRaw?.product_sizes) ? psRaw.product_sizes : []));
            const warehousesData = Array.isArray(whResp?.data) ? whResp.data : Array.isArray(whResp) ? whResp : [];

            setSuppliers(suppliersData);
            setCustomers(allCustomers);
            setLocations(locationsData);
            setProductSizes(productSizesData);
            setWarehouses(warehousesData);

            // Pre-select primary warehouse and matching location for new orders
            if (!order && !orderId) {
                const primaryWh = warehousesData.find(w => 
                    (w.name || '').toLowerCase().includes('primary') || 
                    (w.name || '').toLowerCase().includes('main') ||
                    (w.alias_name || '').toLowerCase().includes('primary')
                ) || warehousesData[0];

                if (primaryWh) {
                    setFormOrder(prev => {
                        if (prev.warehouse_id) return prev;
                        const updates = { warehouse_id: primaryWh.id };
                        
                        // Try to find a matching location for this warehouse
                        const whName = (primaryWh.name || '').toLowerCase();
                        const primaryLoc = locationsData.find(l => 
                            (l.delivery_location || '').toLowerCase().includes('primary') ||
                            (l.delivery_location || '').toLowerCase().includes(whName)
                        ) || locationsData[0];

                        if (primaryLoc) {
                            updates.delivery_location_id = primaryLoc.id;
                        }
                        return { ...prev, ...updates };
                    });
                }
            }

            cache.suppliers = suppliersData;
            cache.customers = allCustomers;
            cache.locations = locationsData;
            cache.productSizes = productSizesData;
            cache.warehouses = warehousesData;
            cache.loaded = true;
        } catch (e) {
            setSuppliers([]);
            setLocations([]);
            setProductSizes([]);
            setWarehouses([]);
        } finally {
            cache.loading = false;
            setDropdownsLoading(false);
        }
    }, [cache, suppliers.length, locations.length, warehouses.length, order, orderId]);

    const totalOrderWeight = useMemo(() => {
        if (!formOrder.items || !Array.isArray(formOrder.items)) return 0;
        return formOrder.items.reduce((sum, it) => {
            const product = getProductForCalc(it.product_size_id);
            const rawPW = parseFloat(it.product_weight);
            const rawSL = parseFloat(it.standard_length);
            const pWeight = !isNaN(rawPW) ? rawPW : (parseFloat(product?.standard_weight) || parseFloat(product?.product_weight) || 0);
            const sLength = (!isNaN(rawSL) && rawSL > 0) ? rawSL : (parseFloat(product?.standard_length) || 6);
            
            const rawQty = parseFloat(it.quantity);
            if (isNaN(rawQty) || (!it.product_size_id && !product)) return sum;

            const unit = (it.unit || '').toLowerCase();
            const len = parseFloat(it.length) || 0;

            let approxW = 0;
            // kg, or a pieces line whose `quantity` carries the optional kg weight.
            // This MUST match the per-line display further down (~line 1605): while
            // the two disagreed, a pieces line showed one weight on the line and
            // contributed a different one to this header total, and freight/loading
            // cost derive from the total.
            if (isWeightUnit(unit) || isPieceUnit(unit)) {
                approxW = rawQty;
            } else if (isLengthUnit(unit)) {
                const requiredLength = len > 0 ? (len * rawQty) : rawQty;
                approxW = sLength > 0 ? (pWeight / sLength) * requiredLength : 0;
            }
            return sum + (Number.isFinite(approxW) ? approxW : 0);
        }, 0);
    }, [formOrder.items, getProductForCalc]);

    // Always keep ref pointing to latest version so form-reset effect can call it without depending on it
    loadDropdownsRef.current = loadDropdowns;

    const handleProductSearch = useCallback((term) => {
        if (productSearchRef.current) clearTimeout(productSearchRef.current);
        productActiveTermRef.current = term;
        if (!term.trim()) {
            setProductSearchLoading(false);
            setProductSizes((prev) => {
                const base = cache.productSizes || [];
                const selectedIds = new Set((formItemsRef.current || []).map(it => String(it.product_size_id)).filter(id => id));
                
                if (selectedIds.size > 0) {
                    const missing = Array.from(selectedIds).filter(id => !base.some(p => String(p.id) === id));
                    if (missing.length > 0) {
                        const selectedItems = prev.filter(p => selectedIds.has(String(p.id)));
                        return [...selectedItems, ...base.filter(p => !selectedIds.has(String(p.id)))];
                    }
                }
                return base;
            });
            return;
        }
        setProductSearchLoading(true);
        productSearchRef.current = setTimeout(async () => {
            try {
                const response = await productSizesService.getAllProductSizes({ search: term, limit: 20 }, { force: true });
                if (productActiveTermRef.current !== term) return;
                const raw = response?.data;
                const list = Array.isArray(raw) ? raw
                    : (Array.isArray(raw?.items) ? raw.items
                    : (Array.isArray(raw?.product_sizes) ? raw.product_sizes : []));
                setProductSizes((prev) => {
                    // Add newly found products to the cache if not already present
                    if (cache.productSizes) {
                        const existingIds = new Set(cache.productSizes.map(p => String(p.id)));
                        const newProducts = list.filter(p => !existingIds.has(String(p.id)));
                        if (newProducts.length > 0) {
                            cache.productSizes = [...cache.productSizes, ...newProducts];
                        }
                    }
                    return list;
                });
            } catch (e) {
                // ignore search errors silently
            } finally {
                if (productActiveTermRef.current === term) setProductSearchLoading(false);
            }
        }, 400);
    }, [cache]);

    const handleSupplierSearch = useCallback((term) => {
        if (supplierSearchRef.current) clearTimeout(supplierSearchRef.current);
        if (!term.trim()) {
            setSupplierSearchLoading(false);
            setSuppliers((prev) => {
                const base = cache.suppliers || prev;
                const selectedId = String(formOrderRef.current?.supplier_id || '');
                if (selectedId && !base.some(s => String(s.id) === selectedId)) {
                    const selected = prev.find(s => String(s.id) === selectedId);
                    return selected ? [selected, ...base] : base;
                }
                return base;
            });
            return;
        }
        setSupplierSearchLoading(true);
        supplierSearchRef.current = setTimeout(async () => {
            try {
                const resp = await customersService.getCustomers({ search: term, limit: 20, is_active: true });
                const list = Array.isArray(resp?.data) ? resp.data : [];
                setSuppliers((prev) => {
                    const selectedId = String(formOrderRef.current?.supplier_id || '');
                    if (selectedId) {
                        const selected = prev.find(s => String(s.id) === selectedId);
                        if (selected && !list.some(s => String(s.id) === selectedId)) {
                            return [selected, ...list];
                        }
                    }
                    return list;
                });
            } catch (e) {
                // ignore search errors silently
            } finally {
                setSupplierSearchLoading(false);
            }
        }, 400);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cache]);

    const handleCustomerSearch = useCallback((term) => {
        if (customerSearchRef.current) clearTimeout(customerSearchRef.current);
        customerActiveTermRef.current = term;
        if (!term.trim()) {
            setCustomerSearchLoading(false);
            setCustomers((prev) => {
                const base = cache.customers || [];
                const selectedId = String(formOrderRef.current?.customer_id || '');
                if (selectedId && !base.some(c => String(c.id) === selectedId)) {
                    const selected = prev.find(c => String(c.id) === selectedId);
                    return selected ? [selected, ...base] : base;
                }
                return base;
            });
            return;
        }
        setCustomerSearchLoading(true);
        customerSearchRef.current = setTimeout(async () => {
            try {
                const resp = await customersService.getCustomers({ search: term, limit: 20, is_active: true });
                if (customerActiveTermRef.current !== term) return;
                const list = Array.isArray(resp?.data) ? resp.data : [];
                setCustomers((prev) => {
                    const selectedId = String(formOrderRef.current?.customer_id || '');
                    if (selectedId) {
                        const selected = prev.find(c => String(c.id) === selectedId);
                        if (selected && !list.some(c => String(c.id) === selectedId)) {
                            return [selected, ...list];
                        }
                    }
                    return list;
                });
            } catch (e) {
                // ignore search errors silently
            } finally {
                if (customerActiveTermRef.current === term) setCustomerSearchLoading(false);
            }
        }, 400);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cache]);

    const handleLocationSearch = useCallback((term) => {
        if (locationSearchRef.current) clearTimeout(locationSearchRef.current);
        locationActiveTermRef.current = term;
        if (!term.trim()) {
            setLocationSearchLoading(false);
            setLocations((prev) => {
                const base = cache.locations || [];
                const selectedId = String(formOrderRef.current?.delivery_location_id || '');
                if (selectedId && !base.some(l => String(l.id) === selectedId)) {
                    const selected = prev.find(l => String(l.id) === selectedId);
                    return selected ? [selected, ...base] : base;
                }
                return base;
            });
            return;
        }
        setLocationSearchLoading(true);
        locationSearchRef.current = setTimeout(async () => {
            try {
                const resp = await locationsService.getLocations({ search: term, limit: 20 });
                if (locationActiveTermRef.current !== term) return;
                const list = resp.success && Array.isArray(resp.data) ? resp.data : [];
                setLocations((prev) => {
                    const selectedId = String(formOrderRef.current?.delivery_location_id || '');
                    if (selectedId) {
                        const selected = prev.find(l => String(l.id) === selectedId);
                        if (selected && !list.some(l => String(l.id) === selectedId)) {
                            return [selected, ...list];
                        }
                    }
                    return list;
                });
            } catch (e) {
                // ignore search errors silently
            } finally {
                if (locationActiveTermRef.current === term) setLocationSearchLoading(false);
            }
        }, 400);
    }, [cache]);

    useEffect(() => {
        if (!isOpen) {
            lastFetchedIdRef.current = null;
            setCurrentOrder(order || null);
        }
    }, [isOpen, order]);

    // Fetch the selected supplier's full detail so GST + complete address can be
    // shown under the dropdown (the list endpoint omits gst_number/address lines).
    useEffect(() => {
        const id = formOrder.supplier_id;
        if (!id) {
            setSelectedSupplierDetail(null);
            return;
        }
        let cancelled = false;
        customersService.getCustomerById(id).then((res) => {
            if (!cancelled && res?.success && res.data) {
                setSelectedSupplierDetail(res.data);
            }
        }).catch(() => {});
        return () => { cancelled = true; };
    }, [formOrder.supplier_id]);

    useEffect(() => {
        const active = currentOrder || order;

        // If we have an orderId and it's different from what we last fetched, fetch it
        if (isOpen && orderId && lastFetchedIdRef.current !== orderId) {
            (async () => {
                try {
                    setLoadingOrder(true);
                    setLoadOrderError(false);
                    const fetched = await fetchOrderById(orderId);
                    await loadDropdownsRef.current();
                    if (fetched) {
                        setCurrentOrder(fetched);
                    } else {
                        // 404 / deleted / network failure — show an error, not an
                        // endless spinner (currentOrder stays null otherwise).
                        setLoadOrderError(true);
                    }
                } finally {
                    setLoadingOrder(false);
                }
            })();
            return;
        }

        setMode(active ? 'existing' : initialMode);
        setStep(0);
        if (active) {
            mapOrderToForm(active);
        } else {
            // Reset form
            setFormOrder({
                supplier_id: '',
                shipping_destination: 'warehouse',
                warehouse_id: '',
                customer_id: '',
                delivery_location_id: '',
                contact_person: '',
                mobile_number: '',
                expected_delivery_date: getTomorrowDate(),
                payment_terms: '',
                discount_type: 'percentage',
                discount_value: '',
                gst_included: false,
                pricing_option: 'all_inclusive',

                sms_notification: false,
                remark: '',
                po_internal_remark: '',
                items: [emptyItem()],
            });
        }
        setNewSupplier(defaultNewSupplier);
        setPendingFiles([]);
        setErrors({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, order, currentOrder, orderId, initialMode, fetchOrderById, defaultNewSupplier]);

    useEffect(() => { if (isOpen) loadDropdowns(); }, [isOpen, loadDropdowns]);

    const handleFormOrderChange = (e) => {
        const { name, value, type, checked } = e.target;
        let v = value;

        // "Use customer's address" is an action, not a location. Resolve it to a real id
        // rather than letting the sentinel reach state, where submit's
        // `Number(delivery_location_id) || 0` would turn it into a silent 0.
        if (name === 'delivery_location_id' && isSyntheticLocationValue(v)) {
            handleCustomerAddressPick();
            return;
        }

        if (type === 'checkbox') v = checked;
        if (['discount_value', 'price', 'quantity', 'length'].includes(name)) v = sanitizeNumber(v);
        setFormOrder(prev => ({ ...prev, [name]: v }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
    };

    // A purchase order's delivery location is where WE receive the goods. On a drop-ship
    // PO (shipping_destination === 'customer') that is the CUSTOMER's site, so their
    // addresses are offered here. The supplier's own address is never offered — it is
    // where goods come from, not where they go. On a warehouse-destination PO this stays
    // null and the dropdown is exactly the master list it has always been.
    useEffect(() => {
        let cancelled = false;
        if (formOrder.shipping_destination !== 'customer' || !formOrder.customer_id) {
            setSelectedCustomerDetail(null);
            return () => { cancelled = true; };
        }
        (async () => {
            const res = await customersService.getCustomerById(formOrder.customer_id);
            if (!cancelled && res?.success) setSelectedCustomerDetail(res.data);
        })();
        return () => { cancelled = true; };
    }, [formOrder.shipping_destination, formOrder.customer_id]);

    const dropShipCustomer = useMemo(() => (
        formOrder.shipping_destination === 'customer' && formOrder.customer_id
            ? customers.find(c => String(c.id) === String(formOrder.customer_id))
            : null
    ), [formOrder.shipping_destination, formOrder.customer_id, customers]);

    const customerAddressDraft = useMemo(() => {
        if (!dropShipCustomer) return null;
        return buildCustomerAddressDraft({ ...dropShipCustomer, ...(selectedCustomerDetail || {}) });
    }, [dropShipCustomer, selectedCustomerDetail]);

    const deliveryLocationOptions = useMemo(() => buildDeliveryLocationOptions({
        warehouses: Array.isArray(dropShipCustomer?.warehouses) ? dropShipCustomer.warehouses : [],
        warehouseShape: 'saved',
        locations,
        selectedId: formOrder.delivery_location_id,
        addressDraft: customerAddressDraft,
        labels: {
            warehouse: '(Warehouse)',
            default: '[Default]',
            useCustomerAddress: t('customerWarehouses.labels.useCustomerAddress'),
            needsDetails: t('customerWarehouses.labels.needsDetails'),
        },
    }), [dropShipCustomer, locations, formOrder.delivery_location_id, customerAddressDraft, t]);

    /** Save a completed draft as a delivery site and select it. */
    const commitCustomerAddress = useCallback(async (draft, siteName) => {
        const result = await resolveCustomerAddress({
            mode: 'existing',
            customerId: formOrder.customer_id,
            draft,
            warehouseName: siteName || dropShipCustomer?.tally_name || dropShipCustomer?.company_name,
        });

        if (!result.success) {
            setErrors(prev => ({ ...prev, delivery_location_id: result.error }));
            return;
        }

        // Prepend explicitly rather than going through handleAddLocationSuccess: that one
        // refetches the unsearched limit-20 list, so a new location outside the first 20
        // would leave the field blank.
        setLocations(prev => (prev.some(l => l.id === result.location.id) ? prev : [result.location, ...prev]));
        if (cache.locations && !cache.locations.some(l => l.id === result.location.id)) {
            cache.locations = [result.location, ...cache.locations];
        }
        setFormOrder(prev => ({ ...prev, delivery_location_id: result.location.id }));
        if (result.warehouse) {
            setCustomers(prev => prev.map(c => (
                String(c.id) === String(formOrder.customer_id)
                    ? { ...c, warehouses: [...(c.warehouses || []),
              // Carry the nested location: the option-builder keys the
              // "already covered" check off it, so without it the address
              // option would linger after being saved.
              { ...result.warehouse, location: result.warehouse?.location || result.location }] }
                    : c
            )));
        }
        setErrors(prev => ({ ...prev, delivery_location_id: null }));
        return result;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formOrder.customer_id, dropShipCustomer, resolveCustomerAddress]);

    /**
     * Picking "use customer's address". Complete addresses save straight through; an
     * incomplete one (no city, which is almost every imported customer) opens the
     * warehouse form prefilled so the missing part is typed once and then remembered.
     */
    const handleCustomerAddressPick = useCallback(() => {
        if (!customerAddressDraft?.offerable || resolving) return;
        if (customerAddressDraft.materializable) {
            commitCustomerAddress(customerAddressDraft);
        } else {
            setShowAddressPrompt(true);
        }
    }, [customerAddressDraft, resolving, commitCustomerAddress]);

    // Memoized: CustomerWarehouseForm re-seeds its fields whenever this prop's IDENTITY
    // changes, so a fresh literal each render would wipe what the user is typing.
    const addressPromptInitialData = useMemo(() => ({
        name: dropShipCustomer?.tally_name || dropShipCustomer?.company_name || '',
        country: customerAddressDraft?.country || 'India',
        state: customerAddressDraft?.state || '',
        city: customerAddressDraft?.city || '',
        address: customerAddressDraft?.delivery_location || '',
    }), [dropShipCustomer, customerAddressDraft]);

    const handleNewSupplierChange = (e) => {
        const { name, value, type, checked } = e.target;
        let v = type === 'checkbox' ? checked : value;
        if (name === 'phone_number_1') {
            v = value.replace(/\D/g, '').slice(0, 10);
        }
        setNewSupplier(prev => ({ ...prev, [name]: v }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
    };

    const handleAddLocationSuccess = async (newLocation) => {
        // Refresh locations
        try {
            const result = await locationsService.getLocations();
            const locationsData = Array.isArray(result?.data) ? result.data : Array.isArray(result) ? result : [];
            setLocations(locationsData);
            cache.locations = locationsData;

            // Auto-select the newly added location
            if (newLocation && newLocation.id) {
                setFormOrder(prev => ({ ...prev, delivery_location_id: newLocation.id }));
                if (errors.delivery_location_id) setErrors(prev => ({ ...prev, delivery_location_id: null }));
            }
        } catch (error) {
            console.error('Failed to refresh locations:', error);
        }
    };

    const handleItemChange = (index, field, value) => {
        setFormOrder(prev => {
            const items = Array.isArray(prev.items) ? [...prev.items] : [];
            const updatedItem = { ...items[index], [field]: ['quantity', 'price', 'length', 'pieces'].includes(field) ? sanitizeNumber(value) : value };

            // Calculate Pieces or Quantity based on each other and product specifications
            const product = getProductForCalc(updatedItem.product_size_id);
            
            // If product changes, capture its standard metrics
            if (field === 'product_size_id' && product) {
                updatedItem.product_weight = String(product.standard_weight || product.product_weight || '');
                updatedItem.standard_length = String(product.standard_length || '');
                // Default length to standard length if not set
                if (!updatedItem.length || updatedItem.length === '6') {
                    updatedItem.length = String(product.standard_length || '6');
                }
            }
            if (field === 'product_size_id') {
                // Auto-fill the line price from the selected SKU's master price
                // (migration 061). Unpriced SKU -> blank line; editable afterwards.
                const skuPrice = product ? (product.base_price ?? product.price) : null;
                updatedItem.price = (skuPrice !== null && skuPrice !== undefined && skuPrice !== '')
                    ? String(skuPrice)
                    : '';
            }

            const pWeight = parseFloat(updatedItem.product_weight) || parseFloat(product?.standard_weight) || parseFloat(product?.product_weight) || 0;
            const sLength = parseFloat(updatedItem.standard_length) || parseFloat(product?.standard_length) || 6;
            const rowLength = parseFloat(updatedItem.length) || sLength;

            if (pWeight > 0 || rowLength > 0) {
                const weightPerPiece = sLength > 0 ? (pWeight / sLength) * rowLength : 0;

                if (field === 'quantity') {
                    const qty = parseFloat(sanitizeNumber(value)) || 0;
                    if (isWeightUnit(updatedItem.unit) && weightPerPiece > 0) {
                        updatedItem.pieces = String(Math.ceil(qty / weightPerPiece));
                    } else if (isLengthUnit(updatedItem.unit) && rowLength > 0) {
                        updatedItem.pieces = String(Math.ceil(qty / rowLength));
                    }
                } else if (field === 'pieces') {
                    const pcs = parseFloat(sanitizeNumber(value)) || 0;
                    const existingQty = parseFloat(updatedItem.quantity) || 0;
                    
                    // Only auto-calculate quantity if quantity is currently empty or 0.
                    // This allows the user to manually tweak the pieces value after entering quantity
                    // without unexpectedly changing the quantity they originally entered.
                    if (existingQty === 0) {
                        if (isWeightUnit(updatedItem.unit) && weightPerPiece > 0) {
                            updatedItem.quantity = (pcs * weightPerPiece).toFixed(2);
                        } else if (isLengthUnit(updatedItem.unit) && rowLength > 0) {
                            updatedItem.quantity = (pcs * rowLength).toFixed(2);
                        }
                    }
                } else if (['unit', 'product_size_id', 'length'].includes(field)) {
                    // Recalculate pieces from quantity (quantity is typically the fixed physical amount ordered)
                    const qty = parseFloat(updatedItem.quantity) || 0;
                    if (qty > 0) {
                        if (isWeightUnit(updatedItem.unit) && weightPerPiece > 0) {
                            updatedItem.pieces = String(Math.ceil(qty / weightPerPiece));
                        } else if (isLengthUnit(updatedItem.unit) && rowLength > 0) {
                            updatedItem.pieces = String(Math.ceil(qty / rowLength));
                        }
                    } else {
                        // Fallback: if quantity was empty but pieces exist, recalculate quantity
                        const pcs = parseFloat(updatedItem.pieces) || 0;
                        if (pcs > 0) {
                            if (isWeightUnit(updatedItem.unit) && weightPerPiece > 0) {
                                updatedItem.quantity = (pcs * weightPerPiece).toFixed(2);
                            } else if (isLengthUnit(updatedItem.unit) && rowLength > 0) {
                                updatedItem.quantity = (pcs * rowLength).toFixed(2);
                            }
                        }
                    }
                }
            }

            items[index] = updatedItem;

            const isAddMode = !order && !orderId && !currentOrder;
            if (isAddMode && index === items.length - 1) {
                // price intentionally omitted — optional now (migration 061).
                const isFilled = updatedItem.product_size_id &&
                                 updatedItem.quantity &&
                                 updatedItem.unit &&
                                 updatedItem.length &&
                                 parseFloat(updatedItem.quantity) > 0;
                
                if (isFilled) {
                    items.push(emptyItem());
                }
            }

            return { ...prev, items };
        });

        const errKey = `items.${index}.${field}`;
        setErrors(prev => {
            if (!prev || !prev[errKey]) return prev || {};
            const copy = { ...prev };
            delete copy[errKey];
            return copy;
        });
    };

    const addItem = () => {
        setFormOrder(prev => {
            const items = Array.isArray(prev.items) ? [...prev.items] : [];
            const lastIndex = Math.max(0, items.length - 1);
            const lastItem = items[lastIndex] || emptyItem();

            const itemValidation = validateOrderItems([lastItem]);
            if (itemValidation && Object.keys(itemValidation).length > 0) {
                const remapped = {};
                Object.keys(itemValidation).forEach(k => {
                    const newKey = k.replace('items.0.', `items.${lastIndex}.`);
                    remapped[newKey] = itemValidation[k];
                });
                setErrors(prevErr => ({ ...prevErr, ...remapped }));
                return prev;
            }

            return { ...prev, items: [...items, emptyItem()] };
        });
    };

    const removeItem = (idx) => setFormOrder(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== idx) }));

    const handleRemoveItem = (idx) => {
        const it = formOrder.items[idx];
        const isExisting = it?.product_size_id && (order || orderId || currentOrder);
        if (isExisting && it.quantity !== undefined && it.price !== undefined) {
            setErrors(prev => ({ ...prev, submit: "Items can't be removed from an existing purchase order. Set quantity to 0 to skip the line instead." }));
            return;
        }
        removeItem(idx);
    };

    const validate = () => {
        let validationErrors = {};
        const isEdit = !!(orderId || currentOrder || order);

        if (mode === 'existing') {
            if (!formOrder.supplier_id) validationErrors.supplier_id = t('purchaseOrdersModule.purchaseOrders.forms.errors.supplierRequired');
        } else {
            if (step === 0) {
                validationErrors = validateNewCustomerData(newSupplier);
                const phone = (newSupplier.phone_number_1 || '').replace(/\D/g, '');
                if (!phone) {
                    validationErrors.phone_number_1 = validationErrors.phone_number_1 || 'Phone number is required';
                } else if (phone.length !== 10) {
                    validationErrors.phone_number_1 = 'Phone number must be exactly 10 digits';
                }
            } else {
                validationErrors = validateNewCustomerData(newSupplier);
            }
        }

        if (mode === 'existing' || (mode === 'new' && step === 1)) {
            if (formOrder.shipping_destination === 'customer') {
                if (!formOrder.customer_id) validationErrors.customer_id = t('purchaseOrdersModule.purchaseOrders.forms.errors.customerRequired');
            } else {
                if (!formOrder.warehouse_id) validationErrors.warehouse_id = t('purchaseOrdersModule.purchaseOrders.forms.errors.warehouseRequired');
            }

            if (!formOrder.delivery_location_id) validationErrors.delivery_location_id = t('purchaseOrdersModule.purchaseOrders.forms.errors.locationRequired');

            if (formOrder.expected_delivery_date) {
                // Backdate validation removed as per user request
            }

            const itemErrors = validateOrderItems(formOrder.items, { isEdit });
            Object.assign(validationErrors, itemErrors);
        }

        setErrors(validationErrors);
        return Object.keys(validationErrors).length === 0;
    };

    const handleNext = () => {
        if (validate()) setStep(1);
    };

    const handleBack = () => {
        setStep(0);
        setErrors({});
    };

    const handleSubmit = async (e) => {
        e && e.preventDefault && e.preventDefault();
        if (submitting) return;            // guard double-submit
        if (!validate()) return;

        setSubmitting(true);
        try {
            const payload = {
                shipping_destination: formOrder.shipping_destination || 'warehouse',
                warehouse_id: (formOrder.shipping_destination === 'warehouse' || !formOrder.shipping_destination) ? Number(formOrder.warehouse_id) : undefined,
                customer_id: formOrder.shipping_destination === 'customer' ? Number(formOrder.customer_id) : undefined,
                // delivery_location_id: Number(formOrder.delivery_location_id), // Kept standard, but verify requirement
                delivery_location_id: Number(formOrder.delivery_location_id) || 0, // Should this be 0 if not selected? Validation ensures selection if required.
                contact_person: formOrder.contact_person,
                mobile_number: formOrder.mobile_number,
                expected_delivery_date: formatDateForPayload(formOrder.expected_delivery_date) || null,
                payment_terms: formOrder.payment_terms ? String(formOrder.payment_terms) : null,
                discount_type: formOrder.discount_type,
                discount_value: formOrder.discount_value ? parseFloat(formOrder.discount_value) : 0,
                gst_included: !!formOrder.gst_included,
                pricing_option: formOrder.pricing_option,

                sms_notification: !!formOrder.sms_notification,
                remark: formOrder.remark || '',
                po_internal_remark: formOrder.po_internal_remark || '',
                items: (formOrder.items || []).filter(it => it.product_size_id).map(it => ({
                    product_size_id: Number(it.product_size_id),
                    unit: it.unit || '',
                    length: it.length ? parseFloat(it.length) : null,
                    quantity: (it.quantity !== '' && it.quantity !== null && it.quantity !== undefined) ? parseFloat(it.quantity) : 0,
                    // price optional (migration 061): blank -> null (unpriced), and a
                    // genuine 0 is preserved rather than coerced to null.
                    price: (it.price !== '' && it.price !== null && it.price !== undefined) ? parseFloat(it.price) : null,
                    remark: it.remark || '',
                    load_by_pieces: !!(it.load_by_pieces),
                    ...(it.pieces ? { quantity_in_pieces: parseInt(it.pieces) } : {}),
                })),
            };

            if (mode === 'existing') {
                payload.supplier_id = Number(formOrder.supplier_id);
            } else {
                Object.assign(payload, {
                    owner_name: newSupplier.owner_name,
                    company_name: newSupplier.company_name,
                    tally_name: newSupplier.tally_name,
                    gst_number: newSupplier.gst_number,
                    ...newSupplier,
                    credit_period: Number(newSupplier.credit_period),
                });
            }

            const isNewOrder = !orderId && !currentOrder?.id && !order?.id;
            if (isNewOrder) {
                const serviceMethod = mode === 'existing'
                    ? purchaseOrderService.createPurchaseOrderExistingSupplierWithAttachments.bind(purchaseOrderService)
                    : purchaseOrderService.createPurchaseOrderNewSupplierWithAttachments.bind(purchaseOrderService);
                const result = await serviceMethod(payload, pendingFiles);
                if (!result.success) throw new Error(result.error || 'Failed to submit purchase order');
                if (onSuccess) onSuccess(result.data); else onClose();
            } else {
                // Edit path: close ONLY on success — a failed update must not discard the edits.
                const result = await onSubmit(payload);
                if (!result || result.success !== true) throw new Error((result && result.error) || 'Failed to submit purchase order');
                onClose();
            }
        } catch (err) {
            const d = err?.response?.data;
            const message = d?.error?.message || d?.message || (typeof d?.error === 'string' ? d.error : null) || err?.message || 'Failed to submit purchase order';
            setErrors(prev => ({ ...prev, submit: message }));
        } finally {
            setSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: UI_COLORS.BACKGROUNDS.OVERLAY }}>
            <style>{`.orders-form-scroll{ -ms-overflow-style: none; scrollbar-width: none; } .orders-form-scroll::-webkit-scrollbar{ display: none; }`}</style>
            <div className="w-full max-w-6xl bg-white rounded-2xl shadow-2xl max-h-[90vh] flex flex-col relative" style={{ backgroundColor: UI_COLORS.BACKGROUNDS.CARD }}>
                {submitting && (
                    <div className="absolute inset-0 z-50 flex items-center justify-center" style={{ backgroundColor: colorHelpers.withOpacity(UI_COLORS.BACKGROUNDS.OVERLAY, 0.6) }}>
                        <div className="h-12 w-12 animate-spin rounded-full border-4 border-b-transparent" style={{ borderColor: colorHelpers.withOpacity(UI_COLORS.BRAND.PRIMARY, 0.6) }}></div>
                    </div>
                )}
                <div className="flex items-center justify-between p-6 border-b flex-shrink-0" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                    <h2 className="text-2xl font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{(orderId || currentOrder || order) ? t('purchaseOrdersModule.purchaseOrders.forms.title.edit') : t('purchaseOrdersModule.purchaseOrders.forms.title.add')}</h2>
                    <button onClick={onClose} className="p-2 rounded-lg" style={{ color: UI_COLORS.TEXT.MUTED, backgroundColor: colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.08) }}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                <div className="overflow-y-auto orders-form-scroll flex-1">
                    {loadOrderError ? (
                        <div className="p-6">
                            <div className="flex flex-col items-center justify-center py-20 text-center">
                                <p className="text-base font-semibold" style={{ color: UI_COLORS.STATUS.ERROR }}>
                                    {t('purchaseOrdersModule.common.loadFailed', 'Could not load this purchase order. It may have been deleted, or the server is unavailable.')}
                                </p>
                                <button type="button" onClick={onClose} className="mt-5 px-6 py-2.5 rounded-xl font-semibold" style={{ backgroundColor: UI_COLORS.BUTTONS.SECONDARY.background, color: UI_COLORS.BUTTONS.SECONDARY.text, border: `2px solid ${UI_COLORS.BUTTONS.SECONDARY.border}` }}>
                                    {t('common.buttons.close', 'Close')}
                                </button>
                            </div>
                        </div>
                    ) : (loadingOrder || (isOpen && orderId && !currentOrder && !order)) ? (
                        <div className="p-6">
                            <div className="flex flex-col items-center justify-center py-20">
                                <div className="h-10 w-10 animate-spin rounded-full border-4 border-b-transparent" style={{ borderColor: colorHelpers.withOpacity(UI_COLORS.BRAND.PRIMARY, 0.5) }}></div>
                                <p className="mt-4 text-sm font-semibold" style={{ color: UI_COLORS.TEXT.SECONDARY }}>{t('purchaseOrdersModule.common.loading')}</p>
                            </div>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="p-6 space-y-6">
                            <div className="space-y-2">
                                <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('purchaseOrdersModule.purchaseOrders.forms.labels.customerMode')}</label>
                                {!(currentOrder || order) ? (
                                    <div className="flex items-center gap-4">
                                        <label className="flex items-center gap-2"><input type="radio" name="mode" checked={mode === 'existing'} onChange={() => setMode('existing')} /> {t('purchaseOrdersModule.purchaseOrders.forms.customerModes.existing')}</label>
                                        <label className="flex items-center gap-2"><input type="radio" name="mode" checked={mode === 'new'} onChange={() => setMode('new')} /> {t('purchaseOrdersModule.purchaseOrders.forms.customerModes.new')}</label>
                                    </div>
                                ) : null}
                            </div>

                            {mode === 'existing' && (
                                <div className="space-y-4">
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div>
                                            <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('purchaseOrdersModule.purchaseOrders.forms.fields.supplier.label')} <span>*</span></label>
                                            <SearchableSelect
                                                name="supplier_id"
                                                value={formOrder.supplier_id}
                                                onChange={(e) => {
                                                    const { value } = e.target;
                                                    const selectedSupplier = suppliers.find(s => String(s.id) === String(value));

                                                    setFormOrder(prev => ({
                                                        ...prev,
                                                        supplier_id: value,
                                                        contact_person: selectedSupplier ? (selectedSupplier.tally_name || selectedSupplier.company_name || prev.contact_person) : prev.contact_person,
                                                        mobile_number: selectedSupplier ? (selectedSupplier.phone_number_1 || prev.mobile_number) : prev.mobile_number,
                                                        payment_terms: selectedSupplier && selectedSupplier.credit_period != null ? String(selectedSupplier.credit_period) : prev.payment_terms,
                                                    }));

                                                    if (errors.supplier_id) setErrors(prev => ({ ...prev, supplier_id: null }));
                                                }}
                                                options={augmentedSuppliers.map(c => ({ value: c.id, label: c.tally_name || c.company_name || c.owner_name || c.name }))}
                                                placeholder={dropdownsLoading ? t('purchaseOrdersModule.purchaseOrders.forms.loading.suppliers') : t('purchaseOrdersModule.purchaseOrders.forms.fields.supplier.placeholder')}
                                                disabled={dropdownsLoading}
                                                error={errors.supplier_id}
                                                onSearchChange={handleSupplierSearch}
                                                loading={supplierSearchLoading}
                                            />
                                            {errors.supplier_id && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.supplier_id}</p>}
                                            {(() => {
                                                if (!formOrder.supplier_id) return null;
                                                const listS = augmentedSuppliers.find(x => String(x.id) === String(formOrder.supplier_id));
                                                const detail = selectedSupplierDetail && String(selectedSupplierDetail.id) === String(formOrder.supplier_id)
                                                    ? selectedSupplierDetail
                                                    : null;
                                                const s = { ...(listS || {}), ...(detail || {}) };
                                                if (!listS && !detail) return null;
                                                const gst = s.gst_number || s.gstn_number || s.gstNumber || '';
                                                const addr = [
                                                    s.address_1 || s.address_line1 || s.address1,
                                                    s.address_2 || s.address_line2 || s.address2,
                                                    s.city,
                                                    s.state,
                                                    s.zip_code || s.zipCode || s.pincode,
                                                ].filter(Boolean).join(', ');
                                                if (!gst && !addr) return null;
                                                return (
                                                    <div className="mt-1 text-xs leading-snug" style={{ color: UI_COLORS.TEXT.MUTED }}>
                                                        {gst && <div><span className="font-medium">{t('commonCols.gstNo')}:</span> {gst}</div>}
                                                        {addr && <div><span className="font-medium">{t('commonCols.address')}:</span> {addr}</div>}
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                        <div>
                                            <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('purchaseOrdersModule.purchaseOrders.forms.fields.contactPerson.label')}</label>
                                            <input name="contact_person" value={formOrder.contact_person} onChange={handleFormOrderChange} placeholder={t('purchaseOrdersModule.purchaseOrders.forms.fields.contactPerson.placeholder')} className="w-full px-4 py-3 rounded-xl border-2" style={{ borderColor: errors.contact_person ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }} />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('purchaseOrdersModule.purchaseOrders.forms.fields.mobileNumber.label')}</label>
                                            <input name="mobile_number" value={formOrder.mobile_number} onChange={handleFormOrderChange} placeholder={t('purchaseOrdersModule.purchaseOrders.forms.fields.mobileNumber.placeholder')} className="w-full px-4 py-3 rounded-xl border-2" style={{ borderColor: errors.mobile_number ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }} />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {mode === 'new' && step === 0 && (
                                <div className="space-y-4">
                                    <h3 className="text-lg font-semibold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{t('purchaseOrdersModule.purchaseOrders.forms.headings.supplierInformation')}</h3>
                                    {/* Reuse new customer fields for new supplier */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('purchaseOrdersModule.customers.forms.fields.ownerName.label')} <span>*</span></label>
                                            <input name="owner_name" value={newSupplier.owner_name} onChange={handleNewSupplierChange} placeholder={t('purchaseOrdersModule.customers.forms.fields.ownerName.label')} className="w-full px-4 py-3 rounded-xl border-2" style={{ borderColor: errors.owner_name ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }} />
                                            {errors.owner_name && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.owner_name}</p>}
                                        </div>
                                        <div>
                                            <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('purchaseOrdersModule.customers.forms.fields.companyName.label')} <span>*</span></label>
                                            <input name="company_name" value={newSupplier.company_name} onChange={handleNewSupplierChange} placeholder={t('purchaseOrdersModule.customers.forms.fields.companyName.label')} className="w-full px-4 py-3 rounded-xl border-2" style={{ borderColor: errors.company_name ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }} />
                                            {errors.company_name && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.company_name}</p>}
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('purchaseOrdersModule.customers.forms.fields.tallyName.label')} <span>*</span></label>
                                            <input name="tally_name" value={newSupplier.tally_name} onChange={handleNewSupplierChange} placeholder={t('purchaseOrdersModule.customers.forms.fields.tallyName.label')} className="w-full px-4 py-3 rounded-xl border-2" style={{ borderColor: errors.tally_name ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }} />
                                            {errors.tally_name && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.tally_name}</p>}
                                        </div>
                                        <div>
                                            <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>
                                                {t('purchaseOrdersModule.customers.forms.fields.gstNumber.label')}
                                                {(newSupplier.isAuthorized !== false && !newSupplier.sister_company) && <span> *</span>}
                                            </label>
                                            <div className="flex items-center gap-4 mt-1">
                                                <label className="flex items-center gap-2 cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        name="isAuthorized"
                                                        checked={!!newSupplier.isAuthorized}
                                                        onChange={handleNewSupplierChange}
                                                        className="h-4 w-4 rounded"
                                                        style={{ accentColor: UI_COLORS.BRAND.PRIMARY }}
                                                    />
                                                    <span className="text-xs font-medium" style={{ color: UI_COLORS.TEXT.SECONDARY }}>Unlisted Dealer</span>
                                                </label>
                                                <label className="flex items-center gap-2 cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        name="sister_company"
                                                        checked={!!newSupplier.sister_company}
                                                        onChange={handleNewSupplierChange}
                                                        className="h-4 w-4 rounded"
                                                        style={{ accentColor: UI_COLORS.BRAND.PRIMARY }}
                                                    />
                                                    <span className="text-xs font-medium" style={{ color: UI_COLORS.TEXT.SECONDARY }}>{t('purchaseOrdersModule.customers.forms.fields.sisterCompany.label')}</span>
                                                </label>
                                            </div>
                                            <input name="gst_number" value={newSupplier.gst_number} onChange={handleNewSupplierChange} placeholder={t('purchaseOrdersModule.customers.forms.fields.gstNumber.label')} className="w-full px-4 py-3 rounded-xl border-2" style={{ borderColor: errors.gst_number ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }} />
                                            {errors.gst_number && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.gst_number}</p>}
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('purchaseOrdersModule.customers.forms.fields.contactPerson.label')} <span>*</span></label>
                                            <input name="contact_person" value={newSupplier.contact_person} onChange={handleNewSupplierChange} placeholder={t('purchaseOrdersModule.customers.forms.fields.contactPerson.label')} className="w-full px-4 py-3 rounded-xl border-2" style={{ borderColor: errors.contact_person ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }} />
                                            {errors.contact_person && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.contact_person}</p>}
                                        </div>
                                        <div>
                                            <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('purchaseOrdersModule.customers.forms.fields.phoneNumber1.label')} <span>*</span></label>
                                            <input name="phone_number_1" value={newSupplier.phone_number_1} onChange={handleNewSupplierChange} placeholder={t('purchaseOrdersModule.customers.forms.fields.phoneNumber1.label')} className="w-full px-4 py-3 rounded-xl border-2" style={{ borderColor: errors.phone_number_1 ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }} />
                                            {errors.phone_number_1 && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.phone_number_1}</p>}
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('purchaseOrdersModule.customers.forms.fields.email.label')} <span>*</span></label>
                                            <input name="email" value={newSupplier.email} onChange={handleNewSupplierChange} placeholder={t('purchaseOrdersModule.customers.forms.fields.email.label')} className="w-full px-4 py-3 rounded-xl border-2" style={{ borderColor: errors.email ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }} />
                                            {errors.email && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.email}</p>}
                                        </div>
                                        <div>
                                            <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('purchaseOrdersModule.customers.forms.fields.creditPeriod.label')} <span>*</span></label>
                                            <input name="credit_period" type="number" value={newSupplier.credit_period} onChange={handleNewSupplierChange} placeholder={t('purchaseOrdersModule.customers.forms.fields.creditPeriod.label')} className="w-full px-4 py-3 rounded-xl border-2" style={{ borderColor: errors.credit_period ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }} />
                                            {errors.credit_period && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.credit_period}</p>}
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('purchaseOrdersModule.customers.forms.fields.address1.label')} <span>*</span></label>
                                            <textarea name="address_line1" value={newSupplier.address_line1} onChange={handleNewSupplierChange} placeholder={t('purchaseOrdersModule.customers.forms.fields.address1.label')} className="w-full px-4 py-3 rounded-xl border-2" style={{ borderColor: errors.address_line1 ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }} />
                                            {errors.address_line1 && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.address_line1}</p>}
                                        </div>
                                        <div>
                                            <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('purchaseOrdersModule.customers.forms.fields.city.label')} <span>*</span></label>
                                            <input name="city" value={newSupplier.city} onChange={handleNewSupplierChange} placeholder={t('purchaseOrdersModule.customers.forms.fields.city.label')} className="w-full px-4 py-3 rounded-xl border-2" style={{ borderColor: errors.city ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }} />
                                            {errors.city && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.city}</p>}
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('purchaseOrdersModule.customers.forms.fields.state.label')} <span>*</span></label>
                                            <SearchableSelect
                                                name="state"
                                                value={newSupplier.state}
                                                onChange={handleNewSupplierChange}
                                                options={INDIAN_STATES}
                                                placeholder={t('purchaseOrdersModule.customers.forms.fields.state.label')}
                                                error={errors.state}
                                            />
                                            {errors.state && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.state}</p>}
                                        </div>
                                        <div>
                                            <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('purchaseOrdersModule.customers.forms.fields.country.label')} <span>*</span></label>
                                            <input name="country" value={newSupplier.country} onChange={handleNewSupplierChange} placeholder={t('purchaseOrdersModule.customers.forms.fields.country.label')} className="w-full px-4 py-3 rounded-xl border-2" style={{ borderColor: errors.country ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }} />
                                            {errors.country && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.country}</p>}
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('purchaseOrdersModule.customers.forms.fields.zipCode.label')} <span>*</span></label>
                                            <input name="zip_code" value={newSupplier.zip_code} onChange={handleNewSupplierChange} placeholder={t('purchaseOrdersModule.customers.forms.fields.zipCode.label')} className="w-full px-4 py-3 rounded-xl border-2" style={{ borderColor: errors.zip_code ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }} />
                                            {errors.zip_code && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.zip_code}</p>}
                                        </div>
                                    </div>

                                    <div className="flex justify-end gap-4 pt-6 border-t" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                                        <button type="button" onClick={onClose} className="px-6 py-3 rounded-xl" style={{ backgroundColor: UI_COLORS.BUTTONS.SECONDARY.background, color: UI_COLORS.BUTTONS.SECONDARY.text }}>{t('purchaseOrdersModule.common.buttons.cancel')}</button>
                                        <button type="button" onClick={handleNext} className="px-6 py-3 rounded-xl font-semibold" style={{ backgroundColor: UI_COLORS.BUTTONS.PRIMARY.background, color: UI_COLORS.BUTTONS.PRIMARY.text }}>{t('purchaseOrdersModule.purchaseOrders.forms.buttons.next')} <FontAwesomeIcon icon={faArrowRight} className="ml-2" /></button>
                                    </div>
                                </div>
                            )}

                            {(mode === 'existing' || (mode === 'new' && step === 1)) && (
                                <div className="space-y-4">
                                    <h3 className="text-lg font-semibold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{t('purchaseOrdersModule.purchaseOrders.forms.headings.orderDetails')}</h3>
                                    {mode === 'new' && (newSupplier.owner_name || newSupplier.company_name) && (
                                        <div className="flex items-center gap-4 px-4 py-2 rounded-lg text-sm" style={{ backgroundColor: colorHelpers.withOpacity(UI_COLORS.BRAND.PRIMARY, 0.06) }}>
                                            {newSupplier.owner_name && (
                                                <span style={{ color: UI_COLORS.TEXT.SECONDARY }}>Supplier: <span className="font-semibold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{newSupplier.owner_name}</span></span>
                                            )}
                                            {newSupplier.company_name && (
                                                <span style={{ color: UI_COLORS.TEXT.SECONDARY }}>Company: <span className="font-semibold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{newSupplier.company_name}</span></span>
                                            )}
                                        </div>
                                    )}

                                    <div className="space-y-2">
                                        <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('purchaseOrdersModule.purchaseOrders.forms.fields.shippingDestination.label')}</label>
                                        <div className="flex items-center gap-4">
                                            <label className="flex items-center gap-2 cursor-pointer">
                                                <input
                                                    type="radio"
                                                    name="shipping_destination"
                                                    value="warehouse"
                                                    checked={formOrder.shipping_destination === 'warehouse'}
                                                    onChange={handleFormOrderChange}
                                                    className="w-4 h-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                                                />
                                                <span className="text-sm">{t('purchaseOrdersModule.purchaseOrders.forms.fields.shippingDestination.options.warehouse')}</span>
                                            </label>
                                            <label className="flex items-center gap-2 cursor-pointer">
                                                <input
                                                    type="radio"
                                                    name="shipping_destination"
                                                    value="customer"
                                                    checked={formOrder.shipping_destination === 'customer'}
                                                    onChange={handleFormOrderChange}
                                                    className="w-4 h-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                                                />
                                                <span className="text-sm">{t('purchaseOrdersModule.purchaseOrders.forms.fields.shippingDestination.options.customer')}</span>
                                            </label>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {formOrder.shipping_destination === 'warehouse' ? (
                                            <div>
                                                <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('purchaseOrdersModule.purchaseOrders.forms.fields.warehouse.label')} <span>*</span></label>
                                                <SearchableSelect
                                                    name="warehouse_id"
                                                    value={formOrder.warehouse_id}
                                                    onChange={handleFormOrderChange}
                                                    options={warehouses.map(w => ({ value: w.id, label: `${w.name || w.alias_name || w.city} ${w.is_active ? '🟢' : '🔴'}` }))}
                                                    placeholder={dropdownsLoading ? t('purchaseOrdersModule.purchaseOrders.forms.loading.warehouses') : t('purchaseOrdersModule.purchaseOrders.forms.fields.warehouse.placeholder')}
                                                    disabled={dropdownsLoading}
                                                    error={errors.warehouse_id}
                                                />
                                                {errors.warehouse_id && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.warehouse_id}</p>}
                                            </div>
                                        ) : (
                                            <div>
                                                <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('purchaseOrdersModule.purchaseOrders.forms.fields.customer.label')} <span>*</span></label>
                                                <SearchableSelect
                                                    name="customer_id"
                                                    value={formOrder.customer_id}
                                                    onChange={handleFormOrderChange}
                                                    options={augmentedCustomers.map(c => ({ value: c.id, label: c.tally_name || c.company_name || c.owner_name || c.name }))}
                                                    placeholder={dropdownsLoading ? 'Loading customers...' : t('purchaseOrdersModule.purchaseOrders.forms.fields.customer.placeholder')}
                                                    disabled={dropdownsLoading}
                                                    error={errors.customer_id}
                                                    onSearchChange={handleCustomerSearch}
                                                    loading={customerSearchLoading}
                                                    noOptionsMessage="Customer not found"
                                                />
                                                {errors.customer_id && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.customer_id}</p>}
                                            </div>
                                        )}
                                        <div>
                                            <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('purchaseOrdersModule.purchaseOrders.forms.fields.deliveryLocation.label')} <span>*</span></label>
                                            <SearchableSelect
                                                name="delivery_location_id"
                                                value={formOrder.delivery_location_id}
                                                onChange={handleFormOrderChange}
                                                options={deliveryLocationOptions}
                                                placeholder={dropdownsLoading ? t('purchaseOrdersModule.purchaseOrders.forms.loading.locations') : t('purchaseOrdersModule.purchaseOrders.forms.fields.deliveryLocation.placeholder')}
                                                // Disabled while resolving so a second click
                                                // cannot create a duplicate delivery site.
                                                disabled={dropdownsLoading || resolving}
                                                error={errors.delivery_location_id}
                                                onSearchChange={handleLocationSearch}
                                                loading={locationSearchLoading || resolving}
                                                onAddNew={(term) => { setLocationAddInitialValue(term); setShowAddLocationModal(true); }}
                                                noOptionsMessage="Location not found"
                                            />
                                        </div>
                                        {errors.delivery_location_id && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.delivery_location_id}</p>}
                                    </div>

                                    {mode === 'new' && (
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('purchaseOrdersModule.purchaseOrders.forms.fields.contactPerson.label')}</label>
                                                <input name="contact_person" value={formOrder.contact_person} onChange={handleFormOrderChange} placeholder={t('purchaseOrdersModule.purchaseOrders.forms.fields.contactPerson.placeholder')} className="w-full px-4 py-3 rounded-xl border-2" />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('purchaseOrdersModule.purchaseOrders.forms.fields.mobileNumber.label')}</label>
                                                <input name="mobile_number" value={formOrder.mobile_number} onChange={handleFormOrderChange} placeholder={t('purchaseOrdersModule.purchaseOrders.forms.fields.mobileNumber.placeholder')} className="w-full px-4 py-3 rounded-xl border-2" />
                                            </div>
                                        </div>
                                    )}

                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div>
                                            <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('purchaseOrdersModule.purchaseOrders.forms.fields.expectedDeliveryDate.label')}</label>
                                            <input type="date" name="expected_delivery_date" value={formOrder.expected_delivery_date} onChange={handleFormOrderChange} className="w-full px-4 py-3 rounded-xl border-2" style={{ borderColor: errors.expected_delivery_date ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }} title={t('common.formFields.labels.expectedDeliveryDate')} />
                                            {errors.expected_delivery_date && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.expected_delivery_date}</p>}
                                        </div>
                                        <div>
                                            <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('purchaseOrdersModule.purchaseOrders.forms.fields.paymentTerms.label')}</label>
                                            <SearchableSelect
                                                name="payment_terms"
                                                value={formOrder.payment_terms}
                                                onChange={handleFormOrderChange}
                                                options={PAYMENT_TERMS_OPTIONS}
                                                placeholder={t('common.formFields.placeholders.selectPaymentTerms')}
                                                allowFreeText={true}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-semibold">&nbsp;</label>
                                            {(order?.id || currentOrder?.id) ? (
                                                <AttachmentsPanel entityType="purchase_order" entityId={order?.id || currentOrder?.id} />
                                            ) : (
                                                <div className="rounded-xl border py-1.5 px-4 flex flex-col justify-center w-full" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                                                    <div className="flex items-center gap-4 w-full">
                                                        <label className="text-sm font-semibold whitespace-nowrap" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('common.formFields.labels.attachments')}</label>
                                                        <div className="flex-1 flex items-center gap-2">
                                                            <input
                                                                ref={pendingFileInputRef}
                                                                type="file"
                                                                multiple
                                                                onChange={e => {
                                                                    const files = Array.from(e.target.files);
                                                                    setPendingFiles(prev => [...prev, ...files]);
                                                                }}
                                                                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                                                            />
                                                            {pendingFiles.length > 0 && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => setShowPendingList(!showPendingList)}
                                                                    className="px-2 py-1 rounded bg-gray-50 hover:bg-gray-100 border text-[11px] font-semibold flex items-center gap-1 transition-colors whitespace-nowrap"
                                                                    style={{ color: UI_COLORS.TEXT.PRIMARY, borderColor: UI_COLORS.BORDERS.LIGHT }}
                                                                >
                                                                    <FontAwesomeIcon icon={showPendingList ? faTimes : faEye} className="text-xs" />
                                                                    {showPendingList ? 'Close' : 'View'}
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                    {pendingFiles.length > 0 && !showPendingList && (
                                                        <p className="mt-1 text-xs" style={{ color: UI_COLORS.TEXT.SECONDARY }}>{pendingFiles.length} file(s) selected — will be uploaded with the order</p>
                                                    )}
                                                    {showPendingList && pendingFiles.length > 0 && (
                                                        <div className="mt-2 border-t pt-2 w-full max-h-40 overflow-y-auto divide-y" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                                                            {pendingFiles.map((file, idx) => (
                                                                <div key={idx} className="flex items-center justify-between py-1 text-xs">
                                                                    <div className="flex items-center gap-2 truncate">
                                                                        <FontAwesomeIcon icon={faPaperclip} className="text-gray-400 text-[10px]" />
                                                                        <span className="font-medium truncate" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{file.name}</span>
                                                                        <span className="text-[10px] text-gray-400">({formatBytes(file.size)})</span>
                                                                    </div>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => {
                                                                            setPendingFiles(prev => {
                                                                                const next = prev.filter((_, i) => i !== idx);
                                                                                if (next.length === 0 && pendingFileInputRef.current) {
                                                                                    pendingFileInputRef.current.value = '';
                                                                                }
                                                                                return next;
                                                                            });
                                                                        }}
                                                                        className="p-1 text-red-500 hover:bg-red-50 rounded"
                                                                    >
                                                                        <FontAwesomeIcon icon={faTrash} className="text-[10px]" />
                                                                    </button>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div className="flex items-center gap-2 pt-2">
                                            <label className="flex items-center gap-2 cursor-pointer">
                                                <input type="checkbox" name="gst_included" checked={!!formOrder.gst_included} onChange={handleFormOrderChange} />
                                                <span>{t('purchaseOrdersModule.purchaseOrders.forms.fields.gstIncluded.label')}</span>
                                            </label>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('purchaseOrdersModule.purchaseOrders.forms.fields.remark.label')}</label>
                                            <textarea name="remark" value={formOrder.remark} onChange={handleFormOrderChange} placeholder={t('purchaseOrdersModule.purchaseOrders.forms.fields.remark.placeholder')} className="w-full px-4 py-3 rounded-xl border-2 h-24" />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('common.formFields.labels.internalRemarks')}</label>
                                            <textarea name="po_internal_remark" value={formOrder.po_internal_remark} onChange={handleFormOrderChange} placeholder={t('common.formFields.placeholders.internalRemarksNotVisibleSupplier')} className="w-full px-4 py-3 rounded-xl border-2 h-24" style={{ borderColor: UI_COLORS.FORMS.INPUT.border, backgroundColor: '#f0fdf4' }} />
                                        </div>
                                    </div>

                                    <div className="space-y-4">
                                        <h4 className="text-sm font-semibold">{t('purchaseOrdersModule.purchaseOrders.forms.fields.items.label')}</h4>
                                        {formOrder.items.map((it, idx) => (
                                            <div key={idx} className="rounded-xl border p-3">
                                                <div className="grid gap-3 items-end grid-cols-6 md:grid-cols-[2fr_0.8fr_0.8fr_0.8fr_0.8fr_0.8fr_auto]">
                                                    <div className="col-span-5 md:col-span-1 order-1 md:order-none">
                                                        <label className="text-xs">{t('purchaseOrdersModule.purchaseOrders.forms.fields.items.productSize.label')} <span>*</span></label>
                                                        <SearchableSelect
                                                            name={`items.${idx}.product_size_id`}
                                                            value={it.product_size_id}
                                                            onChange={(e) => handleItemChange(idx, 'product_size_id', e.target.value)}
                                                            options={(() => {
                                                                const results = productSizes || [];
                                                                const currentId = String(it.product_size_id);
                                                                if (!currentId) return results.map(p => ({ value: p.id, label: p.alias_name || p.product_name || p.name }));
                                                                const isIdInResults = results.some(p => String(p.id) === currentId);
                                                                if (isIdInResults) return results.map(p => ({ value: p.id, label: p.alias_name || p.product_name || p.name }));
                                                                const currentItem = (cache.productSizes || []).find(p => String(p.id) === currentId) || (currentOrder?.items || []).find(i => String(i.product_size_id) === currentId);
                                                                const rowOptions = currentItem ? [currentItem, ...results] : results;
                                                                // product_size_id FIRST: this list can contain an ORDER ITEM (the fallback
                                                                // above searches currentOrder.items), whose `id` is the line
                                                                // item's own id, not the product's. Taking p.id there produced an
                                                                // option the select could never match, so an edited order showed
                                                                // the placeholder instead of its saved product.
                                                                // Label order matters as much as the value. An ORDER ITEM has no
                                                                // alias_name, and its `product_name` is the CATALOGUE name — the
                                                                // same string for every size of that product, so every row read
                                                                // "GC SHEETS". Its `size_name` IS the product's alias_name, so it
                                                                // must come before product_name. (OrdersForm already does this.)
                                                                return rowOptions.map(p => ({ value: p.product_size_id ?? p.id, label: (p.alias_name || p.size_name || p.product_name || p.name) }));
                                                            })()}
                                                            placeholder={t('purchaseOrdersModule.purchaseOrders.forms.fields.items.productSize.placeholder')}
                                                            error={errors[`items.${idx}.product_size_id`]}
                                                            className="text-sm"
                                                            onSearchChange={(term) => handleProductSearch(term)}
                                                            loading={productSearchLoading}
                                                            noOptionsMessage="Item not found"
                                                        />
                                                        {errors[`items.${idx}.product_size_id`] && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors[`items.${idx}.product_size_id`]}</p>}
                                                    </div>
                                                    <div className="col-span-2 md:col-span-1 order-3 md:order-none">
                                                        <label className="text-xs">{t('purchaseOrdersModule.purchaseOrders.forms.fields.items.unit.label')}</label>
                                                        <select value={it.unit} onChange={(e) => handleItemChange(idx, 'unit', e.target.value)} className="w-full px-3 py-2 rounded border-2" style={{ borderColor: errors[`items.${idx}.unit`] ? UI_COLORS.STATUS.ERROR : undefined }}>
                                                            {Object.entries(t('purchaseOrdersModule.common.units', { returnObjects: true }) || {}).map(([key, label]) => (
                                                                <option key={key} value={key}>{label}</option>
                                                            ))}
                                                        </select>
                                                        {errors[`items.${idx}.unit`] && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors[`items.${idx}.unit`]}</p>}
                                                    </div>
                                                    <div className="col-span-2 md:col-span-1 order-5 md:order-none">
                                                        <label className="text-xs">{t('purchaseOrdersModule.purchaseOrders.forms.fields.items.quantity.label')} <span>*</span></label>
                                                        <input value={it.quantity} onChange={(e) => handleItemChange(idx, 'quantity', e.target.value)} placeholder={t('purchaseOrdersModule.purchaseOrders.forms.fields.items.quantity.placeholder')} className="w-full px-3 py-2 rounded border-2" style={{ borderColor: errors[`items.${idx}.quantity`] ? UI_COLORS.STATUS.ERROR : undefined }} />
                                                        {errors[`items.${idx}.quantity`] && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors[`items.${idx}.quantity`]}</p>}
                                                    </div>
                                                    <div className="col-span-3 md:col-span-1 order-6 md:order-none">
                                                        <label className="text-xs">{t('purchaseOrdersModule.purchaseOrders.forms.fields.items.pieces.label')}</label>
                                                        <input value={it.pieces} onChange={(e) => handleItemChange(idx, 'pieces', e.target.value)} placeholder={t('purchaseOrdersModule.purchaseOrders.forms.fields.items.pieces.placeholder')} className="w-full px-3 py-2 rounded border-2" style={{ borderColor: errors[`items.${idx}.pieces`] ? UI_COLORS.STATUS.ERROR : undefined }} />
                                                        {/* A pcs line is priced per piece, so validate.js requires a count and
                                                            writes it to `items.N.pieces` (PO items carry `pieces`, orders carry
                                                            `quantity_in_pieces`). Without this the error was invisible: Submit
                                                            and Add item simply stopped working with nothing on screen. */}
                                                        {errors[`items.${idx}.pieces`] && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors[`items.${idx}.pieces`]}</p>}
                                                    </div>
                                                    <div className="col-span-3 md:col-span-1 order-7 md:order-none">
                                                        <label className="text-xs">{t('purchaseOrdersModule.purchaseOrders.forms.fields.items.price.label')}</label>
                                                        <input value={it.price} onChange={(e) => handleItemChange(idx, 'price', e.target.value)} placeholder={t('purchaseOrdersModule.purchaseOrders.forms.fields.items.price.placeholder')} className="w-full px-3 py-2 rounded border-2" style={{ borderColor: errors[`items.${idx}.price`] ? UI_COLORS.STATUS.ERROR : undefined }} />
                                                        {errors[`items.${idx}.price`] && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors[`items.${idx}.price`]}</p>}
                                                    </div>
                                                    <div className="col-span-2 md:col-span-1 order-4 md:order-none">
                                                        <label className="text-xs">{t('purchaseOrdersModule.purchaseOrders.forms.fields.items.length.label')}</label>
                                                        <input value={it.length} onChange={(e) => handleItemChange(idx, 'length', e.target.value)} placeholder={t('purchaseOrdersModule.purchaseOrders.forms.fields.items.length.placeholder')} className="w-full px-3 py-2 rounded border-2" />
                                                        {errors[`items.${idx}.length`] && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors[`items.${idx}.length`]}</p>}
                                                    </div>
                                                    <div className="col-span-1 md:col-span-1 order-2 md:order-none flex items-end justify-center pb-0.5">
                                                        <button type="button" onClick={() => handleRemoveItem(idx)} className="p-2 rounded bg-red-50 text-red-600 w-full flex justify-center md:w-auto"><FontAwesomeIcon icon={faTrash} /></button>
                                                    </div>
                                                </div>
                                                <div className="mt-1.5 flex gap-4 text-xs text-gray-500">
                                                    <span>{t('purchaseOrdersModule.purchaseOrders.forms.fields.items.approxWeight')}: <strong className="text-gray-700">{(() => {
                                                        const product = getProductForCalc(it.product_size_id);
                                                        const rawPW = parseFloat(it.product_weight);
                                                        const rawSL = parseFloat(it.standard_length);
                                                        const pWeight = !isNaN(rawPW) ? rawPW : (parseFloat(product?.standard_weight) || parseFloat(product?.product_weight) || 0);
                                                        const sLength = (!isNaN(rawSL) && rawSL > 0) ? rawSL : (parseFloat(product?.standard_length) || 6);
                                                        if (!it.product_size_id && !product) return '-';
                                                        if (it.unit !== 'kg' && it.unit !== 'm' && it.unit !== 'pieces' && it.unit !== 'pcs') return '-';
                                                        const rawQty = parseFloat(it.quantity);
                                                        if (isNaN(rawQty)) return '-';
                                                        const qty = rawQty;
                                                        const len = parseFloat(it.length) || 0;
                                                        let approxW = 0;
                                                        if (isWeightUnit(it.unit) || isPieceUnit(it.unit)) {
                                                            // kg, or a pieces line whose `quantity` carries the optional kg weight.
                                                            approxW = qty;
                                                        } else if (isLengthUnit(it.unit)) {
                                                            const requiredLength = len > 0 ? (len * qty) : qty;
                                                            approxW = sLength > 0 ? (pWeight / sLength) * requiredLength : 0;
                                                        }
                                                        return Number.isFinite(approxW) ? `${approxW.toFixed(3)} kg` : '-';
                                                    })()}</strong></span>
                                                </div>
                                                <div className="mt-2 flex items-center gap-4">
                                                    <div className="flex-1">
                                                        <label className="text-xs font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('purchaseOrdersModule.purchaseOrders.forms.fields.items.remark.label')}</label>
                                                        <input
                                                            value={it.remark}
                                                            onChange={(e) => handleItemChange(idx, 'remark', e.target.value)}
                                                            placeholder={t('purchaseOrdersModule.purchaseOrders.forms.fields.items.remark.placeholder')}
                                                            className="w-full px-3 py-2 rounded-lg border-2 transition-all duration-200 focus:shadow-md"
                                                            style={{
                                                                backgroundColor: '#fef9c3',
                                                                borderColor: it.remark ? '#fde047' : UI_COLORS.FORMS.INPUT.border,
                                                                color: UI_COLORS.TEXT.PRIMARY
                                                            }}
                                                        />
                                                    </div>
                                                    <label className="flex items-center gap-1.5 cursor-pointer mt-4 flex-shrink-0">
                                                        <input
                                                            type="checkbox"
                                                            checked={!!(it.load_by_pieces)}
                                                            onChange={(e) => handleItemChange(idx, 'load_by_pieces', e.target.checked)}
                                                            className="h-4 w-4 rounded"
                                                            style={{ accentColor: UI_COLORS.BRAND.PRIMARY }}
                                                        />
                                                        <span className="text-xs font-semibold" style={{ color: UI_COLORS.TEXT.SECONDARY }}>Load by Pieces</span>
                                                    </label>
                                                </div>
                                            </div>
                                        ))}
                                        <div>
                                            <button type="button" onClick={addItem} className="px-4 py-2 rounded-xl" style={{ backgroundColor: UI_COLORS.BUTTONS.SECONDARY.background, color: UI_COLORS.BUTTONS.SECONDARY.text }}><FontAwesomeIcon icon={faPlus} className="mr-2" />{t('purchaseOrdersModule.purchaseOrders.forms.buttons.addItem')}</button>
                                        </div>
                                    </div>

                                    <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4 pt-6 border-t" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                                        {totalOrderWeight > 0 && (
                                            <div className="text-lg font-bold border-b pb-4 md:border-b-0 md:pb-0" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                                                Total Weight: {totalOrderWeight.toFixed(3)} kg
                                            </div>
                                        )}
                                        <div className="flex items-center gap-3 w-full md:w-auto">
                                            {mode === 'new' && (
                                                <button type="button" onClick={handleBack} className="px-5 py-3 rounded-xl font-semibold flex-1 md:flex-initial text-center border" style={{ backgroundColor: UI_COLORS.BUTTONS.SECONDARY.background, color: UI_COLORS.BUTTONS.SECONDARY.text }}><FontAwesomeIcon icon={faArrowLeft} className="mr-2" /> {t('ordersModule.orders.forms.buttons.back')}</button>
                                            )}
                                            <button type="button" onClick={onClose} className="px-5 py-3 rounded-xl font-semibold flex-1 md:flex-initial text-center border" style={{ backgroundColor: UI_COLORS.BUTTONS.SECONDARY.background, color: UI_COLORS.BUTTONS.SECONDARY.text }}>{t('purchaseOrdersModule.common.buttons.cancel')}</button>
                                            <button type="submit" className="px-5 py-3 rounded-xl font-semibold flex-1 md:flex-initial text-center" style={{ backgroundColor: UI_COLORS.BUTTONS.PRIMARY.background, color: UI_COLORS.BUTTONS.PRIMARY.text }}>
                                                <span className="inline-flex items-center justify-center w-full">
                                                    <FontAwesomeIcon icon={faSave} className="mr-2" />
                                                    {(orderId || currentOrder || order) ? t('purchaseOrdersModule.purchaseOrders.forms.buttons.submit.edit') : t('purchaseOrdersModule.purchaseOrders.forms.buttons.submit.add')}
                                                </span>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </form>
                    )}

                </div>
            </div>
            {showAddLocationModal && (
                <AddLocationModal
                    isOpen={showAddLocationModal}
                    onClose={() => { setShowAddLocationModal(false); setLocationAddInitialValue(''); }}
                    onSuccess={handleAddLocationSuccess}
                    initialValues={{ delivery_location: locationAddInitialValue }}
                />
            )}

            {/* Completes the drop-ship customer's address before it becomes a delivery
                site. Prefilled from their record; the missing part (almost always city)
                is typed once and then remembered as a saved site. */}
            <CustomerWarehouseForm
                isOpen={showAddressPrompt}
                onClose={() => setShowAddressPrompt(false)}
                isOffline={true}
                initialData={addressPromptInitialData}
                onSave={async (data) => {
                    setShowAddressPrompt(false);
                    // The modal also offers "use an existing location". Picking that
                    // returns a location_id with the address fields blank, so there is
                    // nothing to resolve — just select it.
                    if (data.location_id) {
                        const picked = locations.find(l => String(l.id) === String(data.location_id));
                        if (picked) {
                            setLocations(prev => (prev.some(l => l.id === picked.id) ? prev : [picked, ...prev]));
                        }
                        setFormOrder(prev => ({ ...prev, delivery_location_id: data.location_id }));
                        setErrors(prev => ({ ...prev, delivery_location_id: null }));
                        return;
                    }
                    await commitCustomerAddress(
                        {
                            materializable: true,
                            delivery_location: data.address,
                            city: data.city,
                            state: data.state,
                            country: data.country || 'India',
                        },
                        data.name
                    );
                }}
            />
        </div>
    );
};

export default PurchaseOrdersForm;
